"use client";

import { cva } from "class-variance-authority";
import { Package, Wrench } from "lucide-react";
import * as React from "react";
import type { JSX } from "react/jsx-runtime";
import { Card, CardContent } from "@/components/ui/atoms/card";
import { Checkbox } from "@/components/ui/atoms/checkbox";
import { ViewToolsControl } from "@/components/ui/atoms/view-tools-control";
import { cn } from "@/lib/utils";
import { RequirementBadges } from "./requirement-badges";

type RequirementsSummary = {
  secrets?: {
    configured: string[];
    pending: string[];
  };
  oauth?: {
    providers: Array<{
      id: string;
      status: "active" | "inactive";
      scopes: string[];
    }>;
  };
};

export type ToolkitCardProps = {
  name: string;
  version?: string;
  description?: string;
  toolsCount: number;
  isSelected?: boolean;
  isPartiallySelected?: boolean;
  selectedToolsCount?: number;
  Icon?: (props: { className?: string }) => JSX.Element;
  requirementsSummary: RequirementsSummary;
  isByoc?: boolean;
  isPro?: boolean;
  onSelect?: (checked: boolean) => void;
  onView?: () => void;
  hideCheckbox?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect">;

const toolkitCardVariants = cva("", {
  variants: {
    selected: {
      true: "border-2 border-emerald-600 dark:border-emerald-400",
      false: "",
    },
    partial: {
      true: "border-2 border-emerald-600 border-dashed dark:border-emerald-400",
      false: "",
    },
  },
  compoundVariants: [
    {
      selected: false,
      partial: false,
      class:
        "border-neutral-200 hover:border-neutral-300 dark:border-neutral-800 dark:hover:border-neutral-700",
    },
  ],
});

const getToolsDisplayText = (
  isPartial: boolean,
  isSelected: boolean,
  selectedCount: number,
  totalCount: number
) => {
  if (isPartial && !isSelected && selectedCount > 0) {
    return `${selectedCount}/${totalCount} tools`;
  }
  return `${totalCount} tool${totalCount === 1 ? "" : "s"}`;
};

const toolsBadgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium text-xs",
  {
    variants: {
      indeterminate: {
        true: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200",
        false:
          "bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200",
      },
    },
  }
);

function ToolkitCardImpl({
  name,
  version,
  toolsCount,
  Icon = (p) => <Package {...p} />,
  isSelected = false,
  isPartiallySelected = false,
  selectedToolsCount = 0,
  requirementsSummary,
  onSelect,
  onView,
  hideCheckbox = false,
  isByoc,
  isPro,
  ...rest
}: ToolkitCardProps) {
  const toggle = React.useCallback(() => {
    if (hideCheckbox && onView) {
      onView();
      return;
    }
    // When partially selected, clicking should deselect all tools
    // When fully selected, clicking should deselect all tools
    // When not selected, clicking should select all tools
    onSelect?.(!(isSelected || isPartiallySelected));
  }, [hideCheckbox, isSelected, isPartiallySelected, onSelect, onView]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const isEnter = e.key === "Enter" || e.code === "Enter";
    const isSpace = e.key === " " || e.key === "Spacebar" || e.code === "Space";
    if (isEnter || isSpace) {
      e.preventDefault();
      toggle();
    }
    if ((e.key === "ArrowRight" || e.code === "ArrowRight") && onView) {
      e.preventDefault();
      onView();
    }
  };

  const isIndeterminate = isPartiallySelected && !isSelected;

  return (
    <Card
      {...rest}
      aria-description="Click card to select this toolkit. Use the View tools control to see its tools."
      aria-label={`Select toolkit ${name}`}
      className={cn(
        "group",
        "relative",
        "h-full",
        "cursor-pointer",
        "overflow-hidden",
        "rounded-lg",
        "border",
        "py-3.5",
        "transition-all",
        "duration-200",
        "bg-white/50",
        "hover:bg-white/70",
        "dark:bg-neutral-900/50",
        "dark:hover:bg-neutral-900/70",
        "hover:shadow-lg",
        toolkitCardVariants({
          selected: isSelected,
          partial: isPartiallySelected,
        })
      )}
      onClick={(e) => {
        if (
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          (e.target as HTMLElement).closest('[data-slot="checkbox-wrapper"]')
        ) {
          return;
        }
        toggle();
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <CardContent className="px-3">
        <div className="space-y-3">
          {/* First row: checkbox, icon/title/version, and tools control */}
          <div
            className={cn(
              "grid items-center gap-2",
              "grid-cols-[20px_1fr_auto]",
              hideCheckbox && "grid-cols-[1fr_auto]"
            )}
          >
            {!hideCheckbox && (
              <div
                className="flex items-center justify-center"
                data-slot="checkbox-wrapper"
              >
                <Checkbox
                  checked={isSelected}
                  indeterminate={isPartiallySelected && !isSelected}
                  onCheckedChange={(checked) => {
                    // When indeterminate or checked, clicking should deselect
                    // When unchecked, clicking should select
                    onSelect?.(checked === true);
                  }}
                  tabIndex={-1}
                />
              </div>
            )}

            {/* Main content: icon + title/version */}
            <div className="mr-1.5 min-w-0">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex size-9 items-center justify-center rounded bg-gradient-to-b from-neutral-200 to-neutral-100 ring-1 ring-black/5 dark:from-neutral-800 dark:to-neutral-900 dark:ring-white/5">
                  <Icon className="size-6 text-neutral-700 dark:text-neutral-200" />
                </div>
                <div className="flex h-9 min-w-0 flex-col justify-center space-y-0.5">
                  <h3 className="truncate font-semibold text-base text-neutral-900 leading-tight dark:text-neutral-100">
                    {name}
                  </h3>
                  {version && (
                    <span className="font-medium text-neutral-500 text-xs leading-tight dark:text-neutral-400">
                      v{version}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right column: Compact View Tools control */}
            <div className="mt-0.5 self-center justify-self-end">
              {onView ? (
                <ViewToolsControl
                  indeterminate={isIndeterminate}
                  name={name}
                  onClick={() => onView()}
                  selectedCount={selectedToolsCount}
                  toolsCount={toolsCount}
                />
              ) : (
                <div
                  className={toolsBadgeVariants({
                    indeterminate: isIndeterminate,
                  })}
                >
                  <Wrench className="h-3 w-3" />
                  <span>
                    {getToolsDisplayText(
                      isPartiallySelected,
                      isSelected,
                      selectedToolsCount,
                      toolsCount
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Second row: badges */}
          <div className={cn(hideCheckbox ? "ml-0" : "ml-8")}>
            <RequirementBadges
              isByoc={isByoc}
              isPro={isPro}
              requirementsSummary={requirementsSummary}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

import { createPropsComparator } from "@/components/ui/utils/memo";

const toolkitCardPropsComparator = createPropsComparator<ToolkitCardProps>([
  "name",
  "version",
  "description",
  "toolsCount",
  "isSelected",
  "isPartiallySelected",
  "selectedToolsCount",
  "Icon",
  "requirementsSummary",
  "isByoc",
  "isPro",
  "hideCheckbox",
]);

export const ToolkitCard = React.memo(
  ToolkitCardImpl,
  toolkitCardPropsComparator
);
